import { nu, type Digits } from './digits.ts';
import type { Locale } from '../locale.ts';

export type Calendar = 'persian' | 'gregory';
export const TEHRAN_TZ = 'Asia/Tehran';

export interface DateFmtCtx {
  locale: Locale;
  digits: Digits;
  calendar: Calendar;
  timeZone?: string;
}

const cache = new Map<string, Intl.DateTimeFormat>();
function dtf(ctx: DateFmtCtx, opts: Intl.DateTimeFormatOptions) {
  const tag = `${ctx.locale === 'fa' ? 'fa-IR' : 'en-US'}-u-ca-${ctx.calendar}-nu-${nu(ctx.digits)}`;
  const full: Intl.DateTimeFormatOptions = { timeZone: ctx.timeZone ?? TEHRAN_TZ, ...opts };
  const key = tag + JSON.stringify(full);
  let f = cache.get(key);
  if (!f) cache.set(key, (f = new Intl.DateTimeFormat(tag, full)));
  return f;
}

const toDate = (d: Date | string | number) => (d instanceof Date ? d : new Date(d));

/** "۲۵ مرداد ۱۴۰۵" | "August 16, 2026" */
export const formatDateLong = (d: Date | string | number, ctx: DateFmtCtx) =>
  dtf(ctx, { year: 'numeric', month: 'long', day: 'numeric' }).format(toDate(d));

/** "۲۵ مرداد" | "Aug 16" */
export const formatDateShort = (d: Date | string | number, ctx: DateFmtCtx) =>
  dtf(ctx, { month: ctx.locale === 'fa' ? 'long' : 'short', day: 'numeric' }).format(toDate(d));

/** "شنبه ۲۵ مرداد" | "Sat, Aug 16" */
export const formatDateWeekday = (d: Date | string | number, ctx: DateFmtCtx) =>
  dtf(ctx, { weekday: ctx.locale === 'fa' ? 'long' : 'short', month: ctx.locale === 'fa' ? 'long' : 'short', day: 'numeric' }).format(toDate(d));

/** "۲۶" | "26" — day of month in the active calendar (calendar-column leads) */
export const formatDayNumber = (d: Date | string | number, ctx: DateFmtCtx) => dtf(ctx, { day: 'numeric' }).format(toDate(d));
/** "مرداد" | "Aug" — month name only */
export const formatMonthName = (d: Date | string | number, ctx: DateFmtCtx) => dtf(ctx, { month: ctx.locale === 'fa' ? 'long' : 'short' }).format(toDate(d));

/** "شنبه" | "Saturday" */
export const formatWeekday = (d: Date | string | number, ctx: DateFmtCtx) =>
  dtf(ctx, { weekday: 'long' }).format(toDate(d));

/** "۱۴:۰۵" | "14:05" — always 24h in Iran. */
export const formatTime = (d: Date | string | number, ctx: DateFmtCtx) =>
  dtf(ctx, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(toDate(d));

/** "۲۵ مرداد، ۱۴:۰۵" */
export const formatDateTime = (d: Date | string | number, ctx: DateFmtCtx) =>
  `${formatDateShort(d, ctx)}${ctx.locale === 'fa' ? '، ' : ', '}${formatTime(d, ctx)}`;

/** Range: "۲۵ تا ۲۸ مرداد" | "Aug 16 – 19" (same month) or full dates otherwise. */
export function formatDateRange(a: Date | string | number, b: Date | string | number, ctx: DateFmtCtx): string {
  const f = dtf(ctx, { month: ctx.locale === 'fa' ? 'long' : 'short', day: 'numeric' });
  // formatRange is widely supported (Safari 14.1+)
  const anyF = f as Intl.DateTimeFormat & { formatRange?: (x: Date, y: Date) => string };
  if (anyF.formatRange) return anyF.formatRange(toDate(a), toDate(b)).replace(/–|—/g, ctx.locale === 'fa' ? ' تا ' : ' – ').replace(/\s+/g, ' ');
  return `${formatDateShort(a, ctx)} – ${formatDateShort(b, ctx)}`;
}

/** Jalali parts for a given instant (year, month 1-12, day) in Tehran time. */
export function jalaliParts(d: Date | string | number, timeZone = TEHRAN_TZ): { year: number; month: number; day: number } {
  const f = new Intl.DateTimeFormat('en-US-u-ca-persian-nu-latn', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric' });
  const parts = Object.fromEntries(f.formatToParts(toDate(d)).map((p) => [p.type, p.value]));
  return { year: +parts.year!, month: +parts.month!, day: +parts.day! };
}

/** ISO date (YYYY-MM-DD) of an instant in Tehran local time — the API's `service_date`/`start_date` convention. */
export function isoDateInTehran(d: Date | string | number): string {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: TEHRAN_TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
  return f.format(toDate(d));
}

/** Days between two ISO dates (b - a). */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86_400_000);
}
